import { useState, useRef, useMemo } from "react";
import { Button, Popover, Select, Typography } from "antd";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import DragIcon from "@/assets/icons/drag_handles.svg?react";
import TableIcon from "@/assets/icons/table.svg?react";
import SearchInput from "@/components/UI/SearchInput";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import { IconButton as ToolbarIconButton } from "../IconButton";
import { useReorderSubsetInPlace } from "@/components/UI/TableToolbar/hooks/useReorder";
import { DndContext } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { normalizedIncludes } from "@/utils/stringUtils";
import {
  SectionHeaderProps,
  IconButtonProps,
  MenuFooterProps,
  SortableSelectedColumnProps,
  AvailableColumnItemProps,
  CategorySectionProps,
  ColumnsMenuProps,
} from "./types";

const { Text } = Typography;

const SectionHeader = ({
  title,
  count,
  actionText,
  actionIcon,
  onAction,
}: SectionHeaderProps) => {
  return (
    <div className="flex justify-between items-center mb-1 mt-1">
      <h2 className="text-sm font-bold">
        <span>
          {title} <span className="text-gray-500">({count})</span>
        </span>
      </h2>
      {onAction && (
        <button
          onClick={onAction}
          className="flex flex-row gap-1 text-[#0798D0]"
        >
          {actionText}
          {actionIcon}
        </button>
      )}
    </div>
  );
};

const IconButton = ({
  icon,
  onClick,
  title,
  disabled = false,
  "data-testid": dataTestId,
}: IconButtonProps) => {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={dataTestId}
      className="disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {icon}
    </button>
  );
};

export const MenuFooter = ({
  onReset,
  onApply,
  resetDisabled = false,
  applyDisabled = false,
}: MenuFooterProps) => {
  return (
    <div className="border-t pt-3 mt-auto flex justify-between">
      <Button
        type="default"
        className="text-[#2B4752] font-semibold"
        onClick={onReset}
        disabled={resetDisabled}
      >
        Reset All
      </Button>
      <Button type="primary" onClick={onApply} disabled={applyDisabled}>
        Apply
      </Button>
    </div>
  );
};

// Sortable Item Component for Selected Columns (Right Panel)
const SortableSelectedColumn = ({
  column,
  columnsTitleCreator,
  onChangeVisibility,
  onToggleLock,
  isSuperuserMode,
}: SortableSelectedColumnProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({
    id: column.fieldName,
    disabled: false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isItemDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-row justify-between pb-2 last:pb-0 text-[#2B4752]"
      data-testid={`selected-column-${column.fieldName}`}
    >
      <div className="flex flex-row gap-2 items-center flex-1 min-w-0">
        <button
          data-testid={`selected-column-drag-${column.fieldName}`}
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          className="cursor-move touch-none shrink-0"
          type="button"
        >
          <DragIcon />
        </button>
        <Text ellipsis={{ tooltip: true }} className="flex-1">
          {columnsTitleCreator(column.fieldName)}
        </Text>
      </div>

      <div className="flex flex-row gap-2 shrink-0">
        {isSuperuserMode && (
          <IconButton
            icon={column.mandatory ? <LockOutlined /> : <UnlockOutlined />}
            onClick={() => onToggleLock(column)}
            title={column.mandatory ? "Unlock Column" : "Lock Column"}
            data-testid={`selected-column-lock-${column.fieldName}`}
          />
        )}

        {!column.mandatory && (
          <IconButton
            icon={<EyeInvisibleOutlined />}
            onClick={() => onChangeVisibility(column, ColumnVisibility.HIDDEN)}
            title="Hide Column"
            data-testid={`selected-column-hide-${column.fieldName}`}
          />
        )}

        {isSuperuserMode && !column.mandatory && (
          <IconButton
            icon={<CloseOutlined />}
            onClick={() =>
              onChangeVisibility(column, ColumnVisibility.NOT_AVAILABLE)
            }
            title="Make Not Available"
            data-testid={`selected-column-remove-${column.fieldName}`}
          />
        )}
      </div>
    </div>
  );
};

// Available Column Item Component (Left Panel)
const AvailableColumnItem = ({
  column,
  columnsTitleCreator,
  isSuperuserMode,
  onChangeVisibility,
  section,
}: AvailableColumnItemProps) => {
  const getActionButtons = () => {
    if (section === "HIDDEN IN TABLE") {
      return (
        <>
          <IconButton
            icon={<EyeOutlined />}
            onClick={() => onChangeVisibility(column, ColumnVisibility.VISIBLE)}
            title="Make Visible"
            data-testid={`hidden-column-show-${column.fieldName}`}
          />
          {isSuperuserMode && (
            <IconButton
              icon={<CloseOutlined />}
              onClick={() =>
                onChangeVisibility(column, ColumnVisibility.NOT_AVAILABLE)
              }
              title="Make Not Available"
              data-testid={`hidden-column-remove-${column.fieldName}`}
            />
          )}
        </>
      );
    }

    if (section === "NOT AVAILABLE IN TABLE") {
      return (
        <>
          <IconButton
            icon={<EyeOutlined />}
            onClick={() => onChangeVisibility(column, ColumnVisibility.VISIBLE)}
            title="Make Visible"
            data-testid={`not-available-column-show-${column.fieldName}`}
          />
          <IconButton
            icon={<EyeInvisibleOutlined />}
            onClick={() => onChangeVisibility(column, ColumnVisibility.HIDDEN)}
            title="Make Hidden"
            data-testid={`not-available-column-hide-${column.fieldName}`}
          />
        </>
      );
    }

    return null;
  };

  return (
    <div
      className="flex flex-row justify-between text-[#2B4752] py-1"
      data-testid={`${section === "HIDDEN IN TABLE" ? "hidden" : "available"}-column-${column.fieldName}`}
    >
      <Text ellipsis={{ tooltip: true }} className="flex-1 pr-2">
        {columnsTitleCreator(column.fieldName)}
      </Text>
      <div className="flex flex-row gap-2 shrink-0">{getActionButtons()}</div>
    </div>
  );
};

// Category Section Component
const CategorySection = ({
  title,
  columns,
  columnsTitleCreator,
  isSuperuserMode,
  onChangeVisibility,
  onToggleAll,
}: CategorySectionProps) => {
  if (columns.length === 0) return null;

  const getToggleAllText = () => {
    if (title === "HIDDEN IN TABLE") return "Add All";
    if (title === "NOT AVAILABLE IN TABLE") return "Activate All";
    return "Add All";
  };

  return (
    <div
      className="border-b border-gray-200 mt-5 last:border-b-0 last:mb-0"
      data-testid={`columns-section-${title === "HIDDEN IN TABLE" ? "hidden" : "selected"}`}
    >
      <SectionHeader
        title={title}
        count={columns.length}
        actionText={getToggleAllText()}
        actionIcon={<ArrowRightOutlined />}
        onAction={onToggleAll}
      />
      <div className="flex flex-col mt-2 gap-1">
        {columns.map((column) => (
          <AvailableColumnItem
            key={column.fieldName}
            column={column}
            columnsTitleCreator={columnsTitleCreator}
            isSuperuserMode={isSuperuserMode}
            onChangeVisibility={onChangeVisibility}
            section={title}
          />
        ))}
      </div>
    </div>
  );
};

export default function ColumnsMenu({
  open,
  setOpen,
  editedColumns,
  setEditedColumns,
  columnsTitleCreator = toTitleCase,
  isSuperuserMode = false,
  selectedColumnCount,
  setSelectedColumnCount,
  changeOneColumn,
  onApply,
  onReset,
  hasChanged = false,
  menuHeight,
  menuWidth,
}: ColumnsMenuProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Filter columns by categories
  const { hiddenColumns, notAvailableColumns, selectedColumns } =
    useMemo(() => {
      if (!editedColumns.length) {
        return {
          hiddenColumns: [],
          notAvailableColumns: [],
          selectedColumns: [],
        };
      }

      const getTitle = (fieldName: string) => columnsTitleCreator(fieldName);

      const hidden: typeof editedColumns = [];
      const notAvailable: typeof editedColumns = [];
      const selected: typeof editedColumns = [];

      for (const col of editedColumns) {
        const title = getTitle(col.fieldName);
        const matchesSearch =
          !searchTerm || normalizedIncludes(title, searchTerm);

        if (col.visibility === ColumnVisibility.VISIBLE) {
          selected.push(col);
        } else if (matchesSearch) {
          if (col.visibility === ColumnVisibility.HIDDEN) hidden.push(col);
          else if (col.visibility === ColumnVisibility.NOT_AVAILABLE)
            notAvailable.push(col);
        }
      }

      return {
        hiddenColumns: hidden,
        notAvailableColumns: notAvailable,
        selectedColumns: selected,
      };
    }, [editedColumns, searchTerm, columnsTitleCreator]);

  // Reorder subset in place: selectedColumns live inside editedColumns
  const {
    sensors,
    handleDragStart,
    handleDragEnd,
    collisionDetection,
    getItemIds,
  } = useReorderSubsetInPlace<ColumnsType>({
    subsetItems: selectedColumns,
    setAllItems: setEditedColumns,
    getItemId: (item) => item.fieldName,
  });

  const handleToggleLock = (column: ColumnsType) => {
    changeOneColumn(column, { mandatory: !column.mandatory });
  };

  const handleChangeVisibility = (
    column: ColumnsType,
    visibility: ColumnVisibility,
  ) => {
    changeOneColumn(column, { visibility });
  };

  const handleAddAllHidden = () => {
    hiddenColumns.forEach((column) => {
      changeOneColumn(column, { visibility: ColumnVisibility.VISIBLE });
    });
  };

  const handleActivateAllNotAvailable = () => {
    notAvailableColumns.forEach((column) => {
      changeOneColumn(column, { visibility: ColumnVisibility.VISIBLE });
    });
  };

  const handleHideAll = () => {
    selectedColumns.forEach((column) => {
      if (!column.mandatory) {
        changeOneColumn(column, { visibility: ColumnVisibility.HIDDEN });
      }
    });
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  const handleReset = () => {
    onReset();
  };

  const handleOpenChange = (newOpen: boolean) => {
    setSearchTerm("");
    setOpen(newOpen);
  };

  const columnIds = getItemIds();

  return (
    <Popover
      content={
        <div
          className="flex flex-col relative"
          style={{ height: menuHeight, width: menuWidth }}
          data-testid="table-columns-menu"
        >
          <div className="flex flex-row flex-1 overflow-hidden">
            {/* Left Panel - Available Columns */}
            <div className="flex flex-col w-1/2 min-w-0 h-full overflow-y-auto border-r px-2 text-[#2B4752] hide-scrollbar">
              <div className="mb-1">
                <SearchInput
                  placeholder="Search columns..."
                  value={searchTerm}
                  onChange={setSearchTerm}
                  testId="table-columns-search"
                />
              </div>

              <div className="flex-1 overflow-y-auto pr-2 hide-scrollbar">
                <CategorySection
                  title="HIDDEN IN TABLE"
                  columns={hiddenColumns}
                  columnsTitleCreator={columnsTitleCreator}
                  isSuperuserMode={isSuperuserMode}
                  onChangeVisibility={handleChangeVisibility}
                  onToggleAll={handleAddAllHidden}
                />

                {isSuperuserMode && (
                  <CategorySection
                    title="NOT AVAILABLE IN TABLE"
                    columns={notAvailableColumns}
                    columnsTitleCreator={columnsTitleCreator}
                    isSuperuserMode={isSuperuserMode}
                    onChangeVisibility={handleChangeVisibility}
                    onToggleAll={handleActivateAllNotAvailable}
                  />
                )}
              </div>
            </div>

            {/* Right Panel - Selected Columns */}
            <div className="flex flex-col w-1/2 min-w-0 h-full pl-4">
              {/* Lock Columns Selector */}
              <div className="mb-3">
                <div className="text-sm text-[#2B4752] flex items-center gap-2 p-[4px_8px] bg-[#0000000a] rounded-sm">
                  <span>Lock up to:</span>
                  <Select
                    value={selectedColumnCount}
                    onSelect={setSelectedColumnCount}
                    size="small"
                    data-testid="table-columns-lock-count-select"
                    options={[0, 1, 2, 3, 4, 5].map((v) => ({
                      label: String(v),
                      value: v,
                    }))}
                  />
                  <span>columns</span>
                  <InfoTooltip
                    title="Select how many columns to keep fixed when scrolling horizontally. Frozen columns remain visible as your scroll sideways, useful for keeping row headers in view."
                    fill="#556C75"
                  />
                </div>
              </div>

              <div className="mt-1 mb-2">
                <SectionHeader
                  title="SHOWN IN TABLE"
                  count={selectedColumns.length}
                  actionText="Hide All"
                  actionIcon={<ArrowLeftOutlined />}
                  onAction={handleHideAll}
                />
              </div>

              <div
                className="flex-1 overflow-y-auto pr-2 hide-scrollbar"
                ref={scrollContainerRef}
              >
                {selectedColumns.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>No columns selected</p>
                    <p className="text-xs mt-1">
                      Add columns from the left panel
                    </p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={collisionDetection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    autoScroll
                  >
                    <SortableContext
                      items={columnIds}
                      strategy={verticalListSortingStrategy}
                    >
                      {selectedColumns.map((column) => (
                        <SortableSelectedColumn
                          key={column.fieldName}
                          column={column}
                          columnsTitleCreator={columnsTitleCreator}
                          onChangeVisibility={handleChangeVisibility}
                          onToggleLock={handleToggleLock}
                          isSuperuserMode={isSuperuserMode}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <MenuFooter
            onReset={handleReset}
            onApply={handleApply}
            applyDisabled={selectedColumns.length === 0}
          />
        </div>
      }
      title={
        <div className="flex items-center justify-between pb-1 border-b">
          <p className="text-base font-semibold">Columns</p>
          <Button
            type="text"
            onClick={() => setOpen(false)}
            icon={<CloseOutlined className="text-[#00000073]" />}
          />
        </div>
      }
      trigger="click"
      placement="bottomRight"
      arrow={false}
      open={open}
      onOpenChange={handleOpenChange}
      autoAdjustOverflow={false}
      align={{ offset: [0, 10] }}
      destroyOnHidden
    >
      <span>
        <ToolbarIconButton
          icon={<TableIcon width={24} height={24} />}
          title="Select and order columns"
          data-testid="table-toolbar-columns-button"
          onClick={() => setOpen(!open)}
          isOpen={open}
          isActive={hasChanged}
          mouseEnterDelay={1}
        />
      </span>
    </Popover>
  );
}
